/**
 * Owner: Masa Bayu
 * File: render-body-layer-v2.tsx
 * Date:Time: 2026-10-15 08:20
 * Description: R11.4/R11.7e body v2 · LINE-02 segments → LiveObject v1.
 */

import type { ReactElement } from 'react';
import {
  groupNativeLinesIntoParagraphs,
  isEngineParagraphGroup,
} from './group-native-paragraphs';
import { writePageDropCapIndex } from '@qx/qx-layout-write';
import {
  isSegmentsJoinOk,
  logLinePaintCardinalityFail,
} from '@qx/qx-line-paint';
import type { AssetLibrary, TextElement } from '../extract/spsu-types';
import { shouldUseLinePaintV2 } from '../feature-flags';
import { DropCapLinePaintedFaceObject } from './dropcap-line-painted-face-object';
import { LinePaintedFaceObject } from './line-painted-face-object';
import { mergeDropCapContinuations } from './merge-dropcap-continuation';
import { LiveObject } from './qx-editor-page-face-objects';
import {
  detectNativeDropCapPairs,
  nativeDropCapSkipIds,
} from './spsu-native-dropcap-face';
import { DropCapJustifiedFaceObject } from './dropcap-justified-face-object';

type BodyLayerOnTextChange = (
  elementId: string,
  newContent: string,
  tipLine?: import('@qx/qx-line-paint').QXWLine,
) => void;

function tipSkipIds(
  letterId: string,
  hostId: string,
  absorbIds: readonly string[],
  flowIds: readonly string[],
): Set<string> {
  return new Set<string>([letterId, hostId, ...absorbIds, ...flowIds]);
}

function paintOrLive(
  element: TextElement,
  assets: AssetLibrary,
  page: { readonly width: number; readonly height: number },
  editable: boolean,
  onTextChange: BodyLayerOnTextChange | undefined,
): ReactElement {
  const joinOk = isSegmentsJoinOk(
    element.content,
    element.linePaint?.segments,
  );
  // R11.7e · LINE-02 + Latin + join · else LiveObject (v1 CSS)
  if (
    element.linePaint !== undefined &&
    shouldUseLinePaintV2(element.linePaint) &&
    joinOk
  ) {
    return (
      <LinePaintedFaceObject
        key={`v2-line-${element.id}`}
        element={element}
        assets={assets}
        page={page}
        editable={editable}
        {...(onTextChange ? { onTextChange } : {})}
      />
    );
  }
  if (
    element.linePaint !== undefined &&
    shouldUseLinePaintV2(element.linePaint) &&
    !joinOk
  ) {
    logLinePaintCardinalityFail({
      lineId: element.id,
      reason: 'SEGMENTS_JOIN_NE_TEXT',
      segmentsN: element.linePaint.segments?.length ?? 0,
    });
  }
  return (
    <LiveObject
      key={`v2-fallback-${element.id}`}
      element={element}
      assets={assets}
      editable={editable}
      {...(onTextChange ? { onTextChange } : {})}
    />
  );
}

/**
 * v2 · L2 line-paint for abs text + dropcap + engine paragraphs.
 * Missing linePaint → LiveObject v1 paint (fail-closed · no invent gaps).
 */
export function renderBodyLayerV2(
  bodyTexts: readonly TextElement[],
  assets: AssetLibrary,
  editable: boolean,
  onTextChange: BodyLayerOnTextChange | undefined,
  pageWidth: number,
  pageHeight: number,
): ReactElement {
  /* §448-S2 · baris dropcap + continuation → satu perenggan · float ::first-letter. */
  const merged = mergeDropCapContinuations(bodyTexts);
  const tipIndex = writePageDropCapIndex({
    elements: merged,
    pageWidth,
  });
  const tipElements = new Map(merged.map((el) => [el.id, el] as const));
  const page = { width: pageWidth, height: pageHeight };

  const paragraphGroups = groupNativeLinesIntoParagraphs(merged);
  const engineGroups = paragraphGroups.filter(isEngineParagraphGroup);
  const engineSkip = new Set<string>();
  for (const group of engineGroups) {
    for (const plate of group) engineSkip.add(plate.id);
  }

  const absoluteSkip = new Set<string>([...engineSkip]);
  const nodes: ReactElement[] = [];
  const legacyPairs = detectNativeDropCapPairs(merged);

  for (let groupIndex = 0; groupIndex < engineGroups.length; groupIndex += 1) {
    const group = engineGroups[groupIndex]!;
    const tipPair = tipIndex.pairs.find((p) =>
      group.some((plate) => plate.id === p.letterId),
    );

    if (tipPair) {
      const letterEl = tipElements.get(tipPair.letterId);
      const hostEl = tipElements.get(tipPair.hostId);
      const latinDrop =
        shouldUseLinePaintV2(letterEl?.linePaint) &&
        shouldUseLinePaintV2(hostEl?.linePaint) &&
        isSegmentsJoinOk(
          letterEl?.content ?? '',
          letterEl?.linePaint?.segments,
        ) &&
        isSegmentsJoinOk(
          hostEl?.content ?? '',
          hostEl?.linePaint?.segments,
        );
      for (const id of tipSkipIds(
        tipPair.letterId,
        tipPair.hostId,
        tipPair.absorbIds,
        tipPair.flowIds,
      )) {
        absoluteSkip.add(id);
      }
      if (latinDrop) {
        nodes.push(
          <DropCapLinePaintedFaceObject
            key={`dropcap-v2-${tipPair.letterId}-${tipPair.hostId}`}
            pair={tipPair}
            tipElements={tipElements}
            assets={assets}
            page={page}
            editable={editable}
            {...(onTextChange ? { onTextChange } : {})}
          />,
        );
      } else {
        // G51 · non-latin tip dropcap → paint plates as LiveObject v1
        for (const plate of group) {
          nodes.push(paintOrLive(plate, assets, page, editable, onTextChange));
        }
      }
      continue;
    }

    const legacyDrop = legacyPairs.find((pair) =>
      group.some((plate) => plate.id === pair.letterId),
    );
    if (legacyDrop) {
      for (const id of nativeDropCapSkipIds([legacyDrop])) absoluteSkip.add(id);
      absoluteSkip.add(legacyDrop.letterId);
      nodes.push(
        <DropCapJustifiedFaceObject
          key={`dropcap-justified-v2q1-${legacyDrop.letterId}`}
          pair={legacyDrop}
          pageTexts={merged}
          paragraphPlates={group}
          assets={assets}
        />,
      );
      continue;
    }

    for (const plate of group) {
      absoluteSkip.add(plate.id);
      nodes.push(paintOrLive(plate, assets, page, editable, onTextChange));
    }
  }

  for (const tipPair of tipIndex.pairs) {
    if (absoluteSkip.has(tipPair.letterId)) continue;
    const letterEl = tipElements.get(tipPair.letterId);
    const hostEl = tipElements.get(tipPair.hostId);
    if (
      !shouldUseLinePaintV2(letterEl?.linePaint) ||
      !shouldUseLinePaintV2(hostEl?.linePaint) ||
      !isSegmentsJoinOk(
        letterEl?.content ?? '',
        letterEl?.linePaint?.segments,
      ) ||
      !isSegmentsJoinOk(
        hostEl?.content ?? '',
        hostEl?.linePaint?.segments,
      )
    ) {
      continue;
    }
    for (const id of tipSkipIds(
      tipPair.letterId,
      tipPair.hostId,
      tipPair.absorbIds,
      tipPair.flowIds,
    )) {
      absoluteSkip.add(id);
    }
    nodes.push(
      <DropCapLinePaintedFaceObject
        key={`dropcap-v2-loose-${tipPair.letterId}`}
        pair={tipPair}
        tipElements={tipElements}
        assets={assets}
        page={page}
        editable={editable}
        {...(onTextChange ? { onTextChange } : {})}
      />,
    );
  }

  for (const element of merged) {
    if (absoluteSkip.has(element.id)) continue;
    nodes.push(paintOrLive(element, assets, page, editable, onTextChange));
  }

  return <>{nodes}</>;
}
